import { useCallback, useEffect, useState } from 'react'
import { addPasskey, credentials, removePasskey, renamePasskey, type Credential, type User } from '../lib/auth'
import { mine } from '../lib/deviceName'
import Icon from './Icon'
import { enablePush, pushState, type PushState } from '../lib/push'
import { get, post } from '../lib/api'
import { t } from '../lib/i18n'

interface Inbox {
  id: string
  title: string
  body: string | null
  target: string | null
  created_at: number
  read_at: number | null
}

/**
 * The account screen: passkeys, notifications, inbox.
 *
 * Device management is not an advanced setting here — with no password, the list
 * of registered passkeys IS the account's security, and the recovery warning is
 * the most important thing on the page.
 */
export default function Account({ user }: { user: User }) {
  const [creds, setCreds] = useState<Credential[]>([])
  const [warning, setWarning] = useState<string | null>(null)
  const [push, setPush] = useState<PushState>('off')
  const [inbox, setInbox] = useState<Inbox[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [c, p, n] = await Promise.all([
      credentials(),
      pushState(),
      get<{ notifications: Inbox[] }>('/api/notifications').catch(() => ({ notifications: [] })),
    ])
    setCreds(c.credentials)
    setWarning(c.warning)
    setPush(p)
    setInbox(n.notifications)
  }, [])

  useEffect(() => { void load() }, [load])

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await load()
    } catch (err) {
      const name = (err as { name?: string }).name
      setError(name === 'NotAllowedError' || name === 'AbortError' ? t('auth.cancelled') : (err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <section className="card">
        <h2>{t('account.passkeys')}</h2>

        {warning && <p className="warn" role="alert">{warning}</p>}

        {/*
          * A passkey list is only useful if the rows can be told apart.
          *
          * This showed "This device" and "Another device" — three rows saying
          * the same two words, which makes it impossible to know which one to
          * remove when a phone is lost. That is not a cosmetic problem: it is a
          * security control nobody can operate.
          *
          * Now: a real name guessed from the browser, the device this one is
          * marked as such, and a rename for when the guess is wrong.
          */}
        <ul className="list devices">
          {creds.map((c) => (
            <DeviceRow
              key={c.id} c={c}
              isThisDevice={mine().includes(c.id)}
              onlyOne={creds.length <= 1}
              busy={busy}
              onRemove={() => run(() => removePasskey(c.id))}
              onRename={(name) => run(() => renamePasskey(c.id, name))}
            />
          ))}
        </ul>

        <button className="primary" disabled={busy} onClick={() => run(() => addPasskey())}>
          {t('account.addPasskey')}
        </button>
        {error && <p className="error" role="alert">{error}</p>}
      </section>

      <section className="card">
        <h2>{t('notify.inbox')}</h2>

        {push === 'blocked' && <p className="warn">{t('notify.blocked')}</p>}
        {push === 'off' && (
          // Must be a real click: Safari refuses the permission prompt outside a
          // user gesture, and refuses it permanently for the origin.
          <button disabled={busy} onClick={() => run(() => enablePush(user.displayName))}>
            {t('notify.enable')}
          </button>
        )}
        {push === 'on' && <p className="muted small">{t('notify.enabled')}</p>}

        <ul className="list">
          {inbox.length === 0 && <li className="muted">{t('notify.empty')}</li>}
          {inbox.map((n) => (
            <li key={n.id} className={n.read_at ? 'read' : ''}>
              <div>
                <strong>{n.title}</strong>
                <div className="muted small">{n.body}</div>
              </div>
              {!n.read_at && (
                <button onClick={() => run(() => post(`/api/notifications/${n.id}/read`))}>Mark read</button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </>
  )
}


function DeviceRow({ c, isThisDevice, onlyOne, busy, onRemove, onRename }: {
  c: Credential
  isThisDevice: boolean
  onlyOne: boolean
  busy: boolean
  onRemove: () => void
  onRename: (name: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(c.deviceName)

  return (
    <li className={`device${isThisDevice ? ' is-this' : ''}`}>
      <div className="device-main">
        {editing ? (
          <form
            className="device-rename"
            onSubmit={(e) => { e.preventDefault(); if (draft.trim()) { onRename(draft.trim()); setEditing(false) } }}
          >
            <label>
              <span className="muted small">{t('device.name')}</span>
              <input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={60} autoFocus />
            </label>
            <button className="primary" disabled={busy || !draft.trim()}>{t('device.save')}</button>
          </form>
        ) : (
          <>
            <strong className="device-name">
              {c.deviceName}
              {/* Knowing which row is the phone in your hand is what stops
                  somebody deleting the passkey they are holding. */}
              {isThisDevice && <em className="this-device">{t('device.thisOne')}</em>}
            </strong>
            <div className="muted small device-meta">
              <span>
                <Icon name={c.syncedToProvider ? 'shield' : 'key'} size={13} />
                {c.syncedToProvider ? t('account.synced') : t('account.deviceOnly')}
              </span>
              {/* A passkey enrolled but never used is one that never worked. */}
              <span>
                <Icon name="clock" size={13} />
                {c.lastUsedAt
                  ? `${t('account.lastUsed')} ${new Date(c.lastUsedAt).toLocaleDateString()}`
                  : t('account.neverUsed')}
              </span>
            </div>
          </>
        )}
      </div>

      {!editing && (
        <div className="device-actions">
          <button className="link" onClick={() => { setDraft(c.deviceName); setEditing(true) }}>
            {t('device.rename')}
          </button>
          <button
            disabled={busy || onlyOne}
            title={onlyOne ? 'This is the only way into the account.' : undefined}
            onClick={onRemove}
          >
            {t('account.removePasskey')}
          </button>
        </div>
      )}
    </li>
  )
}
